import { useState, type ReactNode } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatRelativeTimeIso,
} from '@yeshuman/sdk'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'

import {
  GateBlockedError,
  usePlatformJobRubric,
  useRubricAction,
  type GoldenGate,
  type JobRubricSection,
  type PlatformActionRun,
  type RubricAction,
  type RubricDiffRow,
  type RubricRevision,
} from '../../hooks/use-platform-area'
import { ActionStatusBadge } from './platform-area-parts'

const CHANGE_BADGE: Record<
  RubricDiffRow['change'],
  { label: string; variant: 'successSoft' | 'destructiveSoft' | 'warningSoft' | 'secondarySoft' }
> = {
  added: { label: 'Added', variant: 'successSoft' },
  removed: { label: 'Removed', variant: 'destructiveSoft' },
  changed: { label: 'Changed', variant: 'warningSoft' },
  unchanged: { label: 'Unchanged', variant: 'secondarySoft' },
}

const STATUS_LABEL: Record<RubricRevision['status'], string> = {
  proposed: 'Draft',
  active: 'Active',
  retired: 'Retired',
}

const pct = (value: number | null | undefined, digits = 0) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}%`

function isGate(gate: RubricRevision['golden_gate'] | undefined): gate is GoldenGate {
  return Boolean(gate && 'passed' in gate)
}

function StepHeading({ index, title, children }: { index: number; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h4 className="flex items-center gap-3 text-sm font-semibold">
        <span
          aria-hidden
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium tabular-nums"
        >
          {index}
        </span>
        {title}
      </h4>
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  )
}

function RunLine({ run }: { run: PlatformActionRun | null }) {
  if (!run) return null
  return (
    <div className="flex flex-wrap items-center gap-2 pl-9 text-sm">
      <ActionStatusBadge status={run.status} />
      <span>{run.summary}</span>
      <span className="text-xs text-muted-foreground">
        {formatRelativeTimeIso(run.finished_at ?? run.started_at)}
        {run.actor_name || run.actor_email ? ` by ${run.actor_name || run.actor_email}` : ''}
      </span>
    </div>
  )
}

function WeightCell({ row }: { row: RubricDiffRow }) {
  if (row.change === 'added') return <span className="tabular-nums">{pct(row.weight_after)}</span>
  if (row.change === 'removed') return <span className="tabular-nums text-muted-foreground line-through">{pct(row.weight_before)}</span>
  if (row.weight_before === row.weight_after) return <span className="tabular-nums">{pct(row.weight_after)}</span>
  return (
    <span className="tabular-nums">
      <span className="text-muted-foreground">{pct(row.weight_before)}</span> → {pct(row.weight_after)}
    </span>
  )
}

function SkillsCell({ row }: { row: RubricDiffRow }) {
  const unchanged = row.skills.filter((s) => !row.skills_added.includes(s) && !row.skills_removed.includes(s))
  if (!row.skills.length && !row.skills_removed.length) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex max-w-[22rem] flex-wrap gap-1">
      {row.change !== 'added' && row.change !== 'removed'
        ? row.skills_added.map((s) => (
            <Badge key={`+${s}`} variant="successSoft">
              + {s}
            </Badge>
          ))
        : null}
      {row.change !== 'removed'
        ? row.skills_removed.map((s) => (
            <Badge key={`-${s}`} variant="destructiveSoft">
              − {s}
            </Badge>
          ))
        : null}
      {(row.change === 'added' || row.change === 'removed' ? row.skills : unchanged).map((s) => (
        <Badge key={s} variant="secondarySoft">
          {s}
        </Badge>
      ))}
    </div>
  )
}

function RubricDiff({ section }: { section: JobRubricSection }) {
  const [showUnchanged, setShowUnchanged] = useState(false)
  const unchanged = section.diff.filter((row) => row.change === 'unchanged').length
  const rows = showUnchanged ? section.diff : section.diff.filter((row) => row.change !== 'unchanged')
  return (
    <div className="space-y-2 pl-9">
      <p className="text-sm text-muted-foreground">
        Draft version {section.draft?.version} compared with the {section.current.label.toLowerCase()}.
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Criterion</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Weight</TableHead>
              <TableHead>Skills</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="align-top">
                  <div className="font-medium">{row.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.kind === 'dimension' ? 'Dimension' : 'Competency'}
                    {row.definition_changed ? ' · wording changed' : ''}
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <Badge variant={CHANGE_BADGE[row.change].variant}>{CHANGE_BADGE[row.change].label}</Badge>
                </TableCell>
                <TableCell className="align-top text-sm">
                  <WeightCell row={row} />
                </TableCell>
                <TableCell className="align-top">
                  <SkillsCell row={row} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {unchanged > 0 ? (
        <Button type="button" variant="link" size="sm" className="px-0" onClick={() => setShowUnchanged(!showUnchanged)}>
          {showUnchanged ? 'Hide unchanged criteria' : `Show ${unchanged} unchanged criteria`}
        </Button>
      ) : null}
    </div>
  )
}

function GateResult({ gate }: { gate: GoldenGate }) {
  return (
    <div className="ml-9 space-y-2 rounded-lg border bg-muted/30 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={gate.passed ? 'successSoft' : 'destructiveSoft'}>{gate.passed ? 'Passed' : 'Blocked'}</Badge>
        <span>
          Agreement with the labelled golden set: {pct(gate.baseline?.agreement_ratio, 1)} today,{' '}
          {pct(gate.candidate?.agreement_ratio, 1)} with this rubric.
        </span>
      </div>
      {gate.reason ? <p className="text-destructive">{gate.reason}</p> : null}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
        <div>
          <dt>Labelled pairs</dt>
          <dd className="tabular-nums text-foreground">{gate.candidate?.labelled ?? '—'}</dd>
        </div>
        <div>
          <dt>Exact band match</dt>
          <dd className="tabular-nums text-foreground">
            {pct(gate.baseline?.exact_ratio)} → {pct(gate.candidate?.exact_ratio)}
          </dd>
        </div>
        <div>
          <dt>Pairwise order</dt>
          <dd className="tabular-nums text-foreground">
            {pct(gate.baseline?.pairwise_order_ratio)} → {pct(gate.candidate?.pairwise_order_ratio)}
          </dd>
        </div>
        <div>
          <dt>Mode</dt>
          <dd className="text-foreground">
            {gate.mode}
            {gate.checked_at ? ` · ${formatRelativeTimeIso(gate.checked_at)}` : ''}
          </dd>
        </div>
      </dl>
    </div>
  )
}

function VersionList({ revisions }: { revisions: RubricRevision[] }) {
  if (!revisions.length) return null
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">Versions</p>
      <ul className="space-y-1 text-sm">
        {revisions.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2">
            <span className="tabular-nums">Version {r.version}</span>
            <Badge variant={r.status === 'active' ? 'successSoft' : 'secondarySoft'}>{STATUS_LABEL[r.status]}</Badge>
            <span className="text-xs text-muted-foreground">
              {r.status === 'active' && r.activated_at
                ? `Activated ${formatRelativeTimeIso(r.activated_at)}${r.approved_by_email ? ` by ${r.approved_by_email}` : ''}`
                : r.status === 'retired' && r.retired_at
                  ? `Retired ${formatRelativeTimeIso(r.retired_at)}`
                  : `Drafted ${formatRelativeTimeIso(r.created_at)}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function PlatformJobRubric({ jobId }: { jobId: string }) {
  const { data: section, isLoading, error } = usePlatformJobRubric(jobId)
  const action = useRubricAction(jobId)
  const pending = action.isPending ? action.variables?.kind : undefined

  const run = (input: RubricAction, done: (s: JobRubricSection) => string) =>
    action.mutate(input, {
      onSuccess: (s) => toast(done(s)),
      onError: (err) =>
        err instanceof GateBlockedError
          ? toast.error(`Not activated. ${err.gate.reason || err.message}`)
          : toast.error(err.message),
    })

  const latest = (s: JobRubricSection, step: keyof JobRubricSection['steps']) => s.steps[step]?.summary ?? 'Done.'

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h3>Rubric</h3>
        </CardTitle>
        <CardDescription>
          How candidates are scored for this job. Draft one from the description, compare it, check it against the
          golden set, then approve it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? <Skeleton className="h-40 w-full" /> : null}
        {error ? <p className="text-sm text-destructive">{(error as Error).message}</p> : null}
        {section ? (
          <>
            {!section.scoring_enabled ? (
              <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" role="note">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                <p>
                  Job rubric scoring is off. You can draft, check and activate a rubric, but scoring keeps using the
                  tenant rubric until job rubric scoring is turned on in the TalentCo config.
                </p>
              </div>
            ) : null}

            <p className="text-sm">
              <span className="text-muted-foreground">In use now: </span>
              {section.current.label} · {section.current.criteria.length} criteria
            </p>

            <div className="space-y-2">
              <StepHeading index={1} title="Draft from the job description">
                <Button
                  type="button"
                  size="sm"
                  variant={section.draft ? 'outline' : 'default'}
                  disabled={action.isPending}
                  onClick={() => run({ kind: 'draft' }, (s) => latest(s, 'draft_job_rubric'))}
                >
                  {pending === 'draft'
                    ? 'Drafting…'
                    : section.draft && !section.draft.source_current
                      ? 'Re-draft from the new description'
                      : section.draft
                        ? 'Draft again'
                        : 'Draft rubric'}
                </Button>
              </StepHeading>
              {section.draft && !section.draft.source_current ? (
                <p className="pl-9 text-sm text-warning">The description changed since this draft. Re-draft it.</p>
              ) : null}
              <RunLine run={section.steps.draft_job_rubric} />
            </div>

            <div className="space-y-2">
              <StepHeading index={2} title="Compare with the current rubric" />
              {section.draft ? (
                <RubricDiff section={section} />
              ) : (
                <p className="pl-9 text-sm text-muted-foreground">Draft a rubric to see what would change.</p>
              )}
            </div>

            <div className="space-y-2">
              <StepHeading index={3} title="Check against the golden set">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!section.draft || action.isPending}
                  onClick={() =>
                    section.draft &&
                    run({ kind: 'check', revisionId: section.draft.id }, (s) => latest(s, 'check_job_rubric'))
                  }
                >
                  {pending === 'check' ? 'Checking…' : 'Run golden-set check'}
                </Button>
              </StepHeading>
              {pending === 'check' || pending === 'activate' ? (
                <p className="pl-9 text-sm text-muted-foreground" role="status">
                  Scoring the golden set with this rubric ({section.gate_mode} mode). This can take a little while.
                </p>
              ) : null}
              {section.draft && isGate(section.draft.golden_gate) ? <GateResult gate={section.draft.golden_gate} /> : null}
            </div>

            <div className="space-y-2">
              <StepHeading index={4} title="Approve and activate, or retire">
                {section.draft ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={action.isPending}
                      onClick={() =>
                        section.draft &&
                        run({ kind: 'retire', revisionId: section.draft.id }, (s) => latest(s, 'retire_job_rubric'))
                      }
                    >
                      {pending === 'retire' ? 'Declining…' : 'Decline draft'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={action.isPending}
                      onClick={() =>
                        section.draft &&
                        run({ kind: 'activate', revisionId: section.draft.id }, (s) => latest(s, 'activate_job_rubric'))
                      }
                    >
                      {pending === 'activate' ? 'Checking and activating…' : 'Approve and activate'}
                    </Button>
                  </>
                ) : null}
                {section.current.source === 'job' && !section.draft ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={action.isPending}
                    onClick={() => {
                      const active = section.revisions.find((r) => r.status === 'active')
                      if (active) run({ kind: 'retire', revisionId: active.id }, (s) => latest(s, 'retire_job_rubric'))
                    }}
                  >
                    {pending === 'retire' ? 'Retiring…' : 'Retire active version'}
                  </Button>
                ) : null}
              </StepHeading>
              {!section.draft && section.current.source === 'tenant' ? (
                <p className="pl-9 text-sm text-muted-foreground">Nothing to approve yet.</p>
              ) : null}
              <RunLine run={section.steps.activate_job_rubric} />
              <RunLine run={section.steps.retire_job_rubric} />
            </div>

            <VersionList revisions={section.revisions} />
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
